// Channel polls (/poll). Three pieces:
//   PollModal — the /poll dialog (question + 2–8 options) → PollsApi.create
//   PollCard  — the in-stream card (rendered by MessageList for metadata kind "poll")
//   PollPin   — the compact pinned copy floating top-right in the chat (collapsible)
// Poll state lives in the message's metadata; voting broadcasts MessageEdited so every
// client's card + pin update live. Tapping your own choice retracts the vote.
import { useState } from "react";
import { PollsApi } from "../hooks/useApi.js";
import Icon from "./Icon.jsx";

export function parsePollMeta(m) {
  try {
    const meta = JSON.parse(m.metadataJson || "");
    return meta?.kind === "poll" ? meta : null;
  } catch { return null; }
}

// Shared question + options + results renderer (card and pin differ only in chrome).
function PollBody({ m, meta, currentUserId, showClose }) {
  const [busy, setBusy] = useState(false);
  const votes = meta.votes || {};
  const total = Object.keys(votes).length;
  const myVote = votes[String(currentUserId)];
  const counts = meta.options.map((_, i) => Object.values(votes).filter((v) => v === i).length);
  const canManage = currentUserId === meta.createdBy;

  const act = async (fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); } catch { /* server broadcasts the truth; a failed tap just does nothing */ }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="poll__question">{meta.question}</div>
      <div className="poll__opts">
        {meta.options.map((opt, i) => {
          const pct = total > 0 ? Math.round((counts[i] / total) * 100) : 0;
          const names = Object.entries(votes).filter(([, v]) => v === i)
            .map(([uid]) => (meta.voterNames || {})[uid] || "?").join(", ");
          return (
            <button
              key={i} type="button"
              className={`poll__opt ${myVote === i ? "is-mine" : ""} ${meta.closed ? "is-closed" : ""}`}
              disabled={busy || meta.closed}
              title={names || undefined}
              onClick={() => act(() => PollsApi.vote(m.id, i))}
            >
              <span className="poll__opt-fill" style={{ width: `${pct}%` }} />
              <span className="poll__opt-label">
                {myVote === i && <Icon name="check" />}
                {opt}
              </span>
              <span className="poll__opt-count">{counts[i] > 0 ? `${counts[i]} · ${pct}%` : ""}</span>
            </button>
          );
        })}
      </div>
      <div className="poll__foot">
        <span className="poll__total">
          {meta.closed ? `Final — ${total} vote${total === 1 ? "" : "s"}` : `${total} vote${total === 1 ? "" : "s"} · tap to vote`}
        </span>
        {showClose && canManage && !meta.closed && (
          <button type="button" className="poll__close-btn" disabled={busy}
                  onClick={() => act(() => PollsApi.close(m.id))}>
            Close poll
          </button>
        )}
      </div>
    </>
  );
}

export function PollCard({ m, currentUserId }) {
  const meta = parsePollMeta(m);
  if (!meta) return null;
  return (
    <div className={`poll-card ${meta.closed ? "poll-card--closed" : ""}`}>
      <div className="poll-card__head">
        <span className="poll-card__icon">📊</span>
        <span className="poll-card__by">{m.senderName} started a poll{meta.closed ? " · closed" : ""}</span>
      </div>
      <PollBody m={m} meta={meta} currentUserId={currentUserId} showClose />
    </div>
  );
}

// The floating pinned copy (top-right of the chat). Collapsed = just the pin + question line.
export function PollPin({ m, currentUserId }) {
  const [open, setOpen] = useState(true);
  const meta = parsePollMeta(m);
  if (!meta || meta.closed) return null;
  return (
    <div className={`poll-pin ${open ? "" : "poll-pin--collapsed"}`}>
      <button type="button" className="poll-pin__head" onClick={() => setOpen((o) => !o)}>
        <span className="poll-pin__icon">📌</span>
        <span className="poll-pin__title">{open ? "Poll" : meta.question}</span>
        <Icon name="chevron-down" />
      </button>
      {open && <div className="poll-pin__body"><PollBody m={m} meta={meta} currentUserId={currentUserId} showClose={false} /></div>}
    </div>
  );
}

export function PollModal({ channel, initialQuestion = "", onClose }) {
  const [question, setQuestion] = useState(initialQuestion);
  const [options, setOptions] = useState(["", ""]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const setOpt = (i, v) => setOptions((o) => o.map((x, j) => (j === i ? v : x)));
  const filled = options.map((o) => o.trim()).filter(Boolean);
  const canCreate = question.trim().length > 0 && filled.length >= 2 && !busy;

  const create = async () => {
    if (!canCreate) return;
    setBusy(true); setError(null);
    try {
      await PollsApi.create(channel.id, question.trim(), filled);
      onClose();
    } catch (e) {
      setError(e?.body?.error || "Could not create the poll.");
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay is-open" onClick={onClose}>
      <div className="modal poll-modal" onClick={(e) => e.stopPropagation()}>
        <header className="poll-modal__head">
          <span className="poll-modal__icon">📊</span>
          <div>
            <h3>New poll</h3>
            <p>Everyone in #{channel?.name} can vote. It stays pinned until you close it.</p>
          </div>
        </header>
        {error && <div className="poll-modal__error">{error}</div>}
        <input
          className="poll-modal__q" value={question} autoFocus maxLength={300}
          placeholder="Ask a question…" onChange={(e) => setQuestion(e.target.value)}
        />
        <div className="poll-modal__opts">
          {options.map((opt, i) => (
            <div className="poll-modal__opt" key={i}>
              <input
                value={opt} maxLength={120} placeholder={`Option ${i + 1}`}
                onChange={(e) => setOpt(i, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }}
              />
              {options.length > 2 && (
                <button type="button" className="poll-modal__rm" aria-label="Remove option"
                        onClick={() => setOptions((o) => o.filter((_, j) => j !== i))}>
                  <Icon name="close" />
                </button>
              )}
            </div>
          ))}
          {options.length < 8 && (
            <button type="button" className="poll-modal__add" onClick={() => setOptions((o) => [...o, ""])}>
              <Icon name="plus" /> Add option
            </button>
          )}
        </div>
        <footer className="poll-modal__foot">
          <button type="button" className="btn btn--soft" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn--primary" onClick={create} disabled={!canCreate}>
            {busy ? "Creating…" : "Create poll"}
          </button>
        </footer>
      </div>
    </div>
  );
}
